/**
 * packRowAcross — THE 1-D row packer for "fit these items along this span".
 *
 * One implementation, three callers: surface-knickknack slots on a desk top
 * (planSurfaceItems), book rows on a bookshelf, snack rows on a shelf (both in
 * the web bookshelf renderer, which imports it through the dorm-styling
 * barrel). Three hand-rolled tier loops already existed elsewhere
 * (dormShoeRackRenderer / dormRollingCartRenderer / curtainRenderer) — don't
 * add a fourth.
 *
 * Pure 1-D arithmetic, convention-free: items that don't fit are DROPPED, not
 * squeezed (the dorm "drop rather than place invalidly" contract), and the
 * packed run is centered in the span.
 *
 * @param {number} spanW  usable span width
 * @param {number[]} itemWidths  in placement priority order
 * @param {object} [opts]
 * @param {number} [opts.gap=0]  spacing between adjacent items
 * @returns {{offsets:number[], placedCount:number}} `offsets[i]` is the CENTER
 *   of accepted item i relative to the SPAN CENTER; items past `placedCount`
 *   were dropped.
 */
/**
 * Width a row occupies once `count` items totalling `used` gains one more of
 * width `w`. THE definition of "does it fit", so the packer below and every
 * dealer that pre-measures a row agree by construction rather than by comment.
 *
 * There were five hand-written copies of this expression, epsilon included, and
 * the code acknowledged it — planSurfaceItems carried a note saying the dealer
 * "accumulates width with the same arithmetic the packer uses, so these agree —
 * but clamp anyway". When two copies disagree the result is silently DROPPED
 * items, which is the worst shape a mismatch can take.
 */
function extendRow(used, count, w, gap) {
  return used + (count > 0 ? gap : 0) + w;
}

/** Does a row of `used`/`count` still fit `w` within `spanW`? */
function rowFits(used, count, w, gap, spanW) {
  return extendRow(used, count, w, gap) <= spanW + 1e-9;
}

function packRowAcross(spanW, itemWidths, opts = {}) {
  const gap = opts.gap || 0;
  const placed = [];
  let used = 0;
  for (const w of itemWidths || []) {
    if (!(w > 0)) break;
    if (!rowFits(used, placed.length, w, gap, spanW)) break;
    used = extendRow(used, placed.length, w, gap);
    placed.push(w);
  }

  let cursor = -used / 2;
  const offsets = placed.map((w, i) => {
    if (i > 0) cursor += gap;
    const center = cursor + w / 2;
    cursor += w;
    return center;
  });

  return { offsets, placedCount: placed.length };
}

/**
 * Deal `items` across `slots` round-robin: one item per slot per pass, each
 * slot taking the FIRST remaining item that still fits it.
 *
 * Two behaviours that are the whole point, and that a greedy fill gets wrong:
 *
 *  - SKIPPING, not stopping at, an item too wide for this slot. It stays in the
 *    pool for a wider slot instead of blocking every narrower item behind it.
 *  - One per slot per pass, so a shared room's two desks each get something
 *    rather than the first desk taking the pile. Filling each host greedily put
 *    all four of a theme's knickknacks on one dresser.
 *
 * Mutates each slot's `chosen` / `used`, which the caller then packs for real.
 *
 * @param {Array<{usable:number, chosen:Array, used:number}>} slots
 * @param {Array} items  pool, in placement priority order; consumed
 * @param {object} opts
 * @param {(item:any)=>number} opts.widthOf
 * @param {number} [opts.gap=0]
 */
function dealAcrossSlots(slots, items, { widthOf, gap = 0 }) {
  const remaining = [...items];
  let dealtThisPass = true;
  while (remaining.length > 0 && dealtThisPass) {
    dealtThisPass = false;
    for (const slot of slots) {
      if (remaining.length === 0) break;
      const idx = remaining.findIndex((item) =>
        rowFits(slot.used, slot.chosen.length, widthOf(item), gap, slot.usable)
      );
      if (idx === -1) continue;
      const [item] = remaining.splice(idx, 1);
      slot.used = extendRow(slot.used, slot.chosen.length, widthOf(item), gap);
      slot.chosen.push(item);
      dealtThisPass = true;
    }
  }
  return slots;
}

module.exports = { packRowAcross, dealAcrossSlots, extendRow, rowFits };
